import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode
} from 'react';

export interface PagerSectionProps {
  id: string;
  title: string;
  children: ReactNode;
}

export function PagerSection({ children }: PagerSectionProps): ReactElement {
  return <>{children}</>;
}

interface SectionPagerProps {
  enabled: boolean;
  children: ReactNode;
}

interface SectionDef {
  id: string;
  title: string;
  content: ReactNode;
}

function readSections(children: ReactNode): SectionDef[] {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement(child)) {
      return [];
    }
    const props = child.props as PagerSectionProps;
    if (!props.id || !props.title) {
      return [];
    }
    return [{ id: props.id, title: props.title, content: props.children }];
  });
}

export function SectionPager({ enabled, children }: SectionPagerProps): ReactElement {
  const sections = readSections(children);
  const [active, setActive] = useState(0);
  const lock = useRef(0);

  useEffect(() => {
    if (active >= sections.length) {
      setActive(Math.max(0, sections.length - 1));
    }
  }, [active, sections.length]);

  const go = useCallback(
    (delta: number) => {
      setActive((i) => Math.min(sections.length - 1, Math.max(0, i + delta)));
    },
    [sections.length]
  );

  const onWheel = (e: React.WheelEvent) => {
    if (!enabled || sections.length < 2) {
      return;
    }
    const now = Date.now();
    if (now - lock.current < 420) {
      e.preventDefault();
      return;
    }
    if (Math.abs(e.deltaY) < 24) {
      return;
    }
    e.preventDefault();
    lock.current = now;
    go(e.deltaY > 0 ? 1 : -1);
  };

  if (!enabled) {
    return <div className="section-pager-stack">{children}</div>;
  }

  const current = sections[active];

  return (
    <div className="section-pager" data-testid="section-pager" onWheel={onWheel}>
      <nav className="section-pager-rail" aria-label="Page sections">
        {sections.map((section, i) => (
          <button
            key={section.id}
            type="button"
            className={`section-pager-btn${i === active ? ' section-pager-btn--active' : ''}`}
            aria-current={i === active ? 'true' : undefined}
            onClick={() => setActive(i)}
          >
            {section.title}
          </button>
        ))}
        <div className="section-pager-step">
          <button type="button" className="secondary" disabled={active === 0} onClick={() => go(-1)}>
            Previous
          </button>
          <button
            type="button"
            className="secondary"
            disabled={active >= sections.length - 1}
            onClick={() => go(1)}
          >
            Next
          </button>
        </div>
      </nav>
      <div className="section-pager-pane" data-testid="section-pager-pane">
        {current ? (
          <>
            <h4 className="form-section-title">{current.title}</h4>
            {current.content}
          </>
        ) : null}
      </div>
    </div>
  );
}
